import React, { memo, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { format, parse } from 'date-fns';
import { isNil } from '@common/utils';
import { SuitabilityHeader } from '@container/customer';

/**
 * 추천펀드 TD
 */
const FundTd = memo(({ invTypeText, targetInvTypeText, fundText }) => {
  const [invTypeTextPriority] = useState({
    위험회피형: 0,
    안정추구형: 1,
    위험중립형: 2,
    적극투자형: 3,
    위험선호형: 4,
  });

  const getIncludePriorityList = useCallback(
    (invTypeTextPriority, invTypeText) => {
      const currentPrioity = invTypeTextPriority[invTypeText];
      const fillArray = (from, to, acc) => {
        return acc.length === to
          ? acc
          : fillArray(from + 1, to, acc.concat(from));
      };
      const result = fillArray(0, currentPrioity, []);

      return result;
    },
    [],
  );

  return (
    <td
      className={`text-center ${getIncludePriorityList(invTypeTextPriority, invTypeText).includes(
        invTypeTextPriority[targetInvTypeText],
      )
        ? 'bg-blue'
        : ''
        }`}
      style={{ whiteSpace: 'pre-line' }}
    >
      {fundText.split('<br />').join('\n')}
    </td>
  );
});

FundTd.propTypes = {
  invTypeText: PropTypes.string.isRequired,
  targetInvTypeText: PropTypes.string.isRequired,
  fundText: PropTypes.string.isRequired,
};

/**
 * 추천 보험상품 목록
 */
const RecommandProductTable = memo(({ prdTypeText }) => (
  <div className="app-box">
    <h2 className="tit-con">추천 보험상품 목록</h2>

    <table className="tbl-sty3">
      <caption>목록</caption>
      <thead>
        <tr>
          <th scope="col" style={{ width: '12%' }}>
            구분
          </th>
          <th scope="col">고객성향</th>
          <th scope="col" style={{ width: '20%' }}>
            추천보험상품군
          </th>
        </tr>
      </thead>

      <tbody>
        <tr className={`${'보장형' === prdTypeText ? 'bg-yellow' : ''}`}>
          <td className="text-center">보장형</td>
          <td>위험에 대비한 보장자산(외화) 마련</td>
          <td className="text-center">외화보장</td>
        </tr>
        <tr className={`${'연금형' === prdTypeText ? 'bg-yellow' : ''}`}>
          <td className="text-center">연금형</td>
          <td>노후를 위한 연금자산 마련</td>
          <td className="text-center">외화연금</td>
        </tr>
        <tr className={`${'저축형' === prdTypeText ? 'bg-yellow' : ''}`}>
          <td className="text-center">저축형</td>
          <td>장기 저축을 통한 목돈 마련</td>
          <td className="text-center">외화저축</td>
        </tr>
        <tr>
          <td className="text-center">부적합</td>
          <td>환율 변동에 따른 재산 증식 등 단기적인 이익 취득 목적</td>
          <td className="text-center">외화보험 가입불가</td>
        </tr>
      </tbody>
    </table>
  </div>
));

RecommandProductTable.propTypes = {
  prdTypeText: PropTypes.string.isRequired,
};

/**
 * 외화보험소비자 성향 안내
 */
const RecommandFundTable = memo(({ invTypeText }) => {
  return (
    <div className="app-box">
      <h2 className="tit-con">외화보험소비자 성향 안내</h2>
      <table className="tbl-sty3">
        <caption>목록</caption>
        <col style={{ width: '12%' }} />
        <col />
        <col style={{ width: '18%' }} />
        <col style={{ width: '18%' }} />
        <col style={{ width: '18%' }} />
        <col style={{ width: '17%' }} />
        <thead>
          <tr>
            <th scope="col">구분</th>
            <th
              className={`th-black ${'위험회피형' === invTypeText ? 'bg-yellow' : ''
                }`}
              scope="col"
            >
              위험회피형
            </th>
            <th
              className={`th-black ${'안정추구형' === invTypeText ? 'bg-yellow' : ''
                }`}
              scope="col"
            >
              안정추구형
            </th>
            <th
              className={`th-black ${'위험중립형' === invTypeText ? 'bg-yellow' : ''
                }`}
              scope="col"
            >
              위험중립형
            </th>
            <th
              className={`th-black ${'적극투자형' === invTypeText ? 'bg-yellow' : ''
                }`}
              scope="col"
            >
              적극투자형
            </th>
            <th
              className={`th-black ${'위험선호형' === invTypeText ? 'bg-yellow' : ''
                }`}
              scope="col"
            >
              위험선호형
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <th scope="row" className="text-center">
              고객성향
            </th>
            <td
              className={`${'위험회피형' === invTypeText ? 'bg-yellow' : ''}`}
            >
              원금손실을 원하지 않음
            </td>
            <td
              className={`${'안정추구형' === invTypeText ? 'bg-yellow' : ''}`}
            >
              원금손실을 최소화하길 원함
            </td>
            <td
              className={`${'위험중립형' === invTypeText ? 'bg-yellow' : ''}`}
            >
              원금손실 위험을 충분히 인식하고 있음
            </td>
            <td
              className={`${'적극투자형' === invTypeText ? 'bg-yellow' : ''}`}
            >
              원금손실 위험 감내 가능함
            </td>
            <td
              className={`${'위험선호형' === invTypeText ? 'bg-yellow' : ''}`}
            >
              아주 높은 수익률을 원함
            </td>
          </tr>

          <tr>
            <th scope="row" className="text-center">
              추천 보험상품군
            </th>
            <td
              className={`${'위험회피형' === invTypeText ? 'bg-yellow' : ''}`}
            >
              추천가능 외화상품 없음
              <br />
              <p className="text-info">* 외화보험 가입불가</p>
            </td>

            <td
              className={`${'위험회피형' !== invTypeText ? 'bg-yellow' : ''}`}
              colSpan="4"
            >
              외화보장, 외화연금, 외화저축
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

RecommandFundTable.propTypes = {
  invTypeText: PropTypes.string.isRequired,
};

/**
 * 진단결과 요약
 */
const ResultSummary = memo(({ prdTypeText, invTypeText }) => {
  return prdTypeText === '부적합' ? (
    <>
      <p class="text-con">
        고객님의 답변을 토대로 보험가입 목적 등을 종합적으로 분석한
        결과 고객님의 보험가입 성향은 외화보험을 체결하는 것이
        <span class="text-large red bold">적합하지 않은 것</span>으로 분석되었습니다.
      </p>
      <p class="text-con mgt-16">
        따라서,「 금융소비자 보호에 관한 법률」 제17조(적합성원칙)에
        따라 우리 회사는 고객님께 외화보험계약을 권유할 수 없습니다.
      </p>
      <p class="text-con mgt-16">
        다만, 고객님께서 외화보험 상품목록의 개별상품을 선택하여
        세부내용을 문의하실 경우, 그에 대한 답변은
        가능합니다.
      </p>
    </>
  ) : (
    <p className="text-con">
      고객님의 답변을 종합적으로 분석한 결과, 고객님의 외화보험상품 성향은
      <span className="text-large orange bold">{` ${prdTypeText} `}</span>
      이며, 외화보험소비자 성향은
      <span className="text-large orange bold">{` ${invTypeText} `}</span>
      입니다.
      <br />
      {`${invTypeText === '위험회피형'
        ? '고객님에게 추천 가능한 외화상품은 없습니다.'
        : '고객님에게 적합한 보험계약은 아래와 같습니다.'
        }`}
    </p>
  );
});

ResultSummary.propTypes = {
  prdTypeText: PropTypes.string,
  invTypeText: PropTypes.string,
};

/**
 * 부적합 판단근거(적합성진단 주요 응답 결과)
 */
const NotSuitableResult = memo(({ notSuitableReasons, testDate }) => {
  return (
    <>
      <div className="app-box">
        <h2 className="tit-con">진단결과</h2>
        <table className="tbl-sty3">
          <caption>목록</caption>
          <col style={{ width: '25%' }} />
          <col />
          <tbody>
            <tr>
              <th scope="row" className="text-center">
                외화보험 상품성향
              </th>
              <td>부적합</td>
            </tr>
            <tr>
              <th scope="row" className="text-center">
                진단일자
              </th>
              <td>
                {format(
                  parse(testDate, 'yyyyMMdd', new Date()),
                  'yyyy년 MM월 dd일',
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="app-box">
        <h2 className="tit-con">부적합 판단근거(적합성진단 주요 응답 결과)</h2>
        <table className="tbl-sty3">
          <caption>목록</caption>
          <col style={{ width: '20%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '10%' }} />
          <col />
          <thead>
            <tr>
              <th scope="col">진단부문</th>
              <th scope="col">질문내용</th>
              <th scope="col">문항</th>
              <th scope="col">부적합 판단 기준</th>
            </tr>
          </thead>
          <tbody>
            {notSuitableReasons
              .filter(obj => ['a', 'b'].includes(obj.index))
              .map((obj, i) => (
                <tr key={i}>
                  {i === 0 && ( // 진단부문 컬럼
                    <td
                      rowSpan={`${String(
                        notSuitableReasons.filter(obj =>
                          ['a', 'b', 'c', 'i', 'j'].includes(obj.index),
                        ).length,
                      )}`}
                    >
                      보험가입목적
                    </td>
                  )}

                  {obj.index === 'a' && (
                    <>
                      <td>가입희망 금융상품</td>
                      <td className="text-center">1번</td>
                      <td>
                        {`“외화보험(외화일반보험, 외화변액보험)” 이외에 타금융상품
                        선택`}
                      </td>
                    </>
                  )}

                  {obj.index === 'b' && (
                    <>
                      <td>외화보험 가입목적</td>
                      <td className="text-center">2번</td>
                      <td>
                        {`“환율변동에 따른 재산 증식 등 단기적인 이익 취득 목적”
                        선택`}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            {notSuitableReasons
              .filter(obj => ['c', 'i', 'j'].includes(obj.index))
              .map((obj, i) => (
                <tr key={i}>
                  {!notSuitableReasons.some(obj =>
                    ['a', 'b'].includes(obj.index),
                  ) && (
                      <td
                        rowSpan={`${String(
                          notSuitableReasons.filter(obj =>
                            ['c', 'i', 'j'].includes(obj.index),
                          ).length,
                        )}`}
                      >
                        보험가입목적
                      </td>
                    )}
                  {i === 0 && ( // 질문내용 컬럼
                    <td
                      rowSpan={`${String(
                        notSuitableReasons.filter(obj =>
                          ['c', 'i', 'j'].includes(obj.index),
                        ).length,
                      )}`}
                    >
                      외화보험 실수요여부
                    </td>
                  )}

                  {obj.index === 'c' && (
                    <>
                      <td className="text-center">3번</td>
                      <td>
                        아래 항목 중 1가지 선택
                        <br />
                        “가끔 해외여행경비 활용”
                        <br />
                        “안정적 재산증식 수단으로 손실가능성이 없는 외화를 활용”
                        선택
                      </td>
                    </>
                  )}

                  {obj.index === 'i' && (
                    <>
                      <td className="text-center">
                        2번,
                        <br />
                        3번
                      </td>
                      <td>
                        2번 문항 “위험에 대비한 보장자산(외화) 마련”
                        <br />
                        3번 문항 아래 항목 중 1가지 선택
                        <br />
                        “장기의 미래에 본인 생활자금 또는 유학계획이 있는 자녀
                        생활자금 등으로 활용”
                        <br />
                        “향후 해외 거주할 예정이며, 준비 또는 생활자금 등으로
                        활용” 선택
                      </td>
                    </>
                  )}

                  {obj.index === 'j' && (
                    <>
                      <td className="text-center">
                        2번,
                        <br />
                        3번
                      </td>
                      <td>
                        2번 문항 “노후를 위한 연금자산 마련”,”장기저축을 통한
                        목돈 마련” 선택
                        <br />
                        3번 문항 아래 항목 중 1가지 선택
                        <br />
                        “장기의 미래에 유족 등에게 외화자산 상속 등 보장자산으로
                        활용”
                        <br />
                        “외화 보장자산 확보를 통한 보장자산 다각화 등 미래
                        위험분산에 활용” 선택
                      </td>
                    </>
                  )}
                </tr>
              ))}

            {notSuitableReasons.map(obj => obj.index).includes('d') && (
              <tr>
                <td>보험료 납입능력</td>
                <td>보험료 납입 가능기간</td>
                <td className="text-center">7번</td>
                <td>“3년 미만” 선택</td>
              </tr>
            )}

            {notSuitableReasons.map(obj => obj.index).includes('e') && (
              <tr>
                <td>보험계약 유지능력</td>
                <td>보험계약 최소 유지기간</td>
                <td className="text-center">8번</td>
                <td>“5년 ~ 7년 미만”, “5년 미만” 선택</td>
              </tr>
            )}

            {notSuitableReasons
              .filter(obj => ['f', 'k'].includes(obj.index))
              .map((obj, i) => (
                <>
                  <tr>
                    {i === 0 && (
                      <>
                        <td
                          rowSpan={`${String(
                            notSuitableReasons.filter(obj =>
                              ['f', 'k'].includes(obj.index),
                            ).length,
                          )}`}
                        >
                          취약금융소비자
                        </td>
                        <td
                          rowSpan={`${String(
                            notSuitableReasons.filter(obj =>
                              ['f', 'k'].includes(obj.index),
                            ).length,
                          )}`}
                        >
                          취약금융소비자 여부
                        </td>
                      </>
                    )}

                    <td className="text-center">-</td>
                    {obj.index === 'f' && (
                      <td>
                        고령취약금융소비자 (만 65세이상)
                        <br />
                        10번 문항 “가입경험 없음” 또는 11번문항 “가입경험 없음”
                        선택
                        <br />
                        14번 문항 “이해수준이 현저히 낮음” 선택
                      </td>
                    )}
                    {obj.index === 'k' && (
                      <td>
                        취약금융소비자 여부 “예”로 체크시 (만 19세 ~ 만 64세)
                      </td>
                    )}
                  </tr>
                </>
              ))}

            {notSuitableReasons.map(obj => obj.index).includes('g') && (
              <tr>
                <td>보장성보험 가입가능 여부</td>
                <td>중도해지 가능성</td>
                <td className="text-center">
                  2번,
                  <br />
                  9번
                </td>
                <td>
                  2번 문항 “위험에 대비한 보장자산(외화) 마련” 선택
                  <br />
                  9번 문항 아래 항목 중 1가지 선택
                  <br />
                  “가능성이 있음(50% 이상)”, “높음(70% 이상)”,
                  <br />
                  “매우 높음(90% 이상)” 답변 선택
                </td>
              </tr>
            )}

            {notSuitableReasons.map(obj => obj.index).includes('h') && (
              <tr>
                <td>보장성보험 가입가능 여부</td>
                <td>외화금융상품 가입경험</td>
                <td className="text-center">
                  2번,
                  <br />
                  10번,
                  <br />
                  11번
                </td>
                <td>
                  2번 문항 “위험에 대비한 보장자산(외화) 마련” 선택
                  <br />
                  10번, 11번 “가입경험없음” 선택
                  <br />
                  3번 문항 “장기의 미래에 유족 등에게 외화자산 상속 등
                  보장자산으로 활용”, “외화 보장자산 확보를 통한 보장자산 다각화
                  등 미래 위험분산에 활용” 선택
                  <br />
                  7번문항 “10년~20년 미만”, “7년~10년 미만”, “3년~7년 미만”,
                  “3년 미만”중 1개 선택
                  <br />
                  8번문항 “10년 ~ 20년 미만”, “7년 ~ 10년 미만”, “5년 ~ 7년
                  미만”, “5년미만” 중 1개 선택
                  <br />→ 상기 3번 문항 중 1개이상 필수 / 7번, 8번문항 은
                  1개라도 해당 될 경우 부적합
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
});

NotSuitableResult.propTypes = {
  testDate: PropTypes.string.isRequired,
  notSuitableReasons: PropTypes.array.isRequired,
};

/**
 * 외화보험 적합성 테스트 결과 확인
 */
const ForeignCurrencySuitabilityTestResult = ({
  customerInfo,
  suitabilityFormData,
  handleNotSuitableTest,
  pageConfirmClick,
  answers,
  isWriting, // 서면 진행 여부
  notSuitableReasons, // 부적합 원인
}) => {
  const isNotSuitable = useMemo(
    () => '부적합' === suitabilityFormData.prdTypeText,
    [suitabilityFormData],
  );

  /**
   * '1,2,3,4,5,1,1'형태의 답변을 {no:value}형태의 object로 변경
   */
  const deserializeAnswers = useCallback((str, delimeter = ',') => {
    const result = str.split(delimeter).reduce((acc, v, i) => {
      // acc[i] = i < 10 ? `0${i}0${v}` : `${i}0${v}`;
      acc[i] = Number(v);
      return acc;
    }, {});
    return result;
  }, []);

  return (
    <>
      <SuitabilityHeader title={'3. 외화보험 적합성 진단 결과 확인'} />
      <div className="con-wrap bg-gray">
        <div className="app-wrap">
          <div className="app-box">
            <h2 className="tit-con">외화보험 적합성 진단 결과</h2>
            <ResultSummary
              prdTypeText={suitabilityFormData.prdTypeText}
              invTypeText={suitabilityFormData.invTypeText}
            />
          </div>
          {isNotSuitable ? (
            <NotSuitableResult
              testDate={suitabilityFormData.testDate}
              notSuitableReasons={notSuitableReasons}
            />
          ) : (
            <>
              <RecommandProductTable
                prdTypeText={suitabilityFormData.prdTypeText}
              />
              <RecommandFundTable
                invTypeText={suitabilityFormData.invTypeText}
              />
            </>
          )}
        </div>
      </div>

      <div className="con-bottom bg-gray box-shadow">
        <div className="btn-area">
          <div className="box-btns-center">
            <button
              type="button"
              className="btn-s yellow btn-wide w-328"
              onClick={() =>
                isNotSuitable
                  ? handleNotSuitableTest()
                  : pageConfirmClick(
                    isWriting ? suitabilityFormData : () => true,
                  )
              }
            >
              {isNotSuitable ? '확인' : '결과 확인 및 작성 완료'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

ForeignCurrencySuitabilityTestResult.propTypes = {
  customerInfo: PropTypes.shape({
    legalAge: PropTypes.number.isRequired,
    custKind: PropTypes.string,
    answers: PropTypes.object,
  }).isRequired,
  suitabilityFormData: PropTypes.shape({
    reportSerialNo: PropTypes.string,
    prdTypeText: PropTypes.string,
    invTypeText: PropTypes.string,
    testDate: PropTypes.string.isRequired,
  }).isRequired,
  handleNotSuitableTest: PropTypes.func.isRequired,
  pageConfirmClick: PropTypes.func.isRequired,
  answers: PropTypes.string.isRequired,
  isWriting: PropTypes.bool,
  notSuitableReasons: PropTypes.array.isRequired,
};

ForeignCurrencySuitabilityTestResult.defaultProps = {
  isWriting: false,
};

export default memo(ForeignCurrencySuitabilityTestResult);
